import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Loader2, MessageSquare, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

export const AdminSMSPackagesPage = () => {
  const { api } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    message_count: 500,
    price: 25,
    rollover_enabled: false,
    validity_days: 30,
    is_active: true
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const data = await apiGet('/api/admin/sms-packages');
      setPackages(data);
    } catch (e) {
      // Try public endpoint if admin fails
      try {
        const data = await apiGet('/api/sms-packages');
        setPackages(data);
      } catch (e2) {
        toast.error('Failed to load SMS packages');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      if (isEditing && selectedPackage) {
        await apiPut('/api/admin/sms-packages/{package_id}', { params: { package_id: selectedPackage.id }, body: form });
        toast.success('Package updated!');
      } else {
        await apiPost('/api/admin/sms-packages', { body: form });
        toast.success('Package created!');
      }
      setShowDialog(false);
      resetForm();
      fetchPackages();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save package'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Deactivate this package? Existing purchases will not be affected.')) return;
    try {
      await apiDelete('/api/admin/sms-packages/{package_id}', { params: { package_id: packageId } });
      toast.success('Package deactivated');
      fetchPackages();
    } catch (e) {
      toast.error('Failed to deactivate package');
    }
  };

  const openEditDialog = (pkg) => {
    setSelectedPackage(pkg);
    setForm({
      name: pkg.name,
      message_count: pkg.message_count,
      price: pkg.price,
      rollover_enabled: pkg.rollover_enabled,
      validity_days: pkg.validity_days,
      is_active: pkg.is_active
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      message_count: 500,
      price: 25,
      rollover_enabled: false,
      validity_days: 30,
      is_active: true
    });
    setIsEditing(false);
    setSelectedPackage(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-sms-packages-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">SMS Packages</h1>
            <p className="text-muted-foreground">Manage SMS credit packages for studios</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-package-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Package
          </Button>
        </div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {packages.map((pkg, i) => (
            <Card 
              key={pkg.id} 
              className={`relative ${!pkg.is_active ? 'opacity-60' : ''}`}
              data-testid={`sms-package-card-${i}`}
            >
              {!pkg.is_active && (
                <Badge variant="secondary" className="absolute top-4 right-4">Inactive</Badge>
              )}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  {pkg.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center py-4 bg-primary/5 rounded-lg">
                  <p className="text-4xl font-bold text-primary">{pkg.message_count.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">SMS Credits</p>
                </div>
                
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-2xl font-bold">€{pkg.price}</span>
                </div>
                
                <div className="space-y-2 py-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span>Price per SMS</span>
                    <span className="font-medium">€{(pkg.price / pkg.message_count).toFixed(4)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Validity</span>
                    <span className="font-medium">{pkg.validity_days} days</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Rollover</span>
                    <span className="font-medium flex items-center gap-1">
                      {pkg.rollover_enabled ? (
                        <><Check className="w-4 h-4 text-emerald-500" /> Yes</>
                      ) : (
                        <><X className="w-4 h-4 text-muted-foreground" /> No</>
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(pkg)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  {pkg.is_active && (
                    <Button variant="outline" size="sm" onClick={() => handleDelete(pkg.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground text-lg mb-4">No SMS packages yet</p>
            <Button onClick={() => setShowDialog(true)}>Create First Package</Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Package' : 'Create SMS Package'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the details of this package' : 'Create a new SMS credit package for studios'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Package Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="e.g., Starter Pack"
                required 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMS Credits</Label>
                <Input 
                  type="number" 
                  value={form.message_count} 
                  onChange={(e) => setForm({ ...form, message_count: parseInt(e.target.value) })} 
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>Price (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={form.price} 
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} 
                  min={0}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Validity (Days)</Label>
              <Input 
                type="number" 
                value={form.validity_days} 
                onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) })} 
                min={1}
              />
              <p className="text-xs text-muted-foreground">How long credits remain valid after purchase</p>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label>Enable Rollover</Label>
                <p className="text-xs text-muted-foreground">Unused credits carry over on renewal</p>
              </div>
              <Switch 
                checked={form.rollover_enabled} 
                onCheckedChange={(checked) => setForm({ ...form, rollover_enabled: checked })} 
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Make this package available for purchase</p>
              </div>
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} 
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditing ? 'Save Changes' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
